import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | anon
  const [settings, setSettings] = useState({ min_margin_percent: 20 });

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get("/settings");
      setSettings({ min_margin_percent: parseFloat(r.data.min_margin_percent) });
    } catch (_) {
      // fallback default
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setStatus("anon");
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        setStatus("authed");
        loadSettings();
      })
      .catch(() => {
        setToken(null);
        setStatus("anon");
      });
  }, [loadSettings]);

  const login = async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    setToken(r.data.access_token);
    setUser(r.data.user);
    setStatus("authed");
    loadSettings();
    return r.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setToken(null);
    setUser(null);
    setStatus("anon");
  };

  return (
    <AuthContext.Provider value={{ user, status, login, logout, setUser, settings, refreshSettings: loadSettings }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function hasRole(user, ...roles) {
  return !!user && roles.flat().includes(user.role);
}
